import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, Chrome, ArrowLeft } from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import SEOHead from '../components/SEOHead';
import { useAnalytics } from '../hooks/useAnalytics';
import logger from '../lib/logger';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithGoogle, currentUser, loading: authLoading } = useAuth();
  const { trackLoginStarted, trackLoginCompleted, trackAuthError } = useAnalytics();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const from = location.state?.from?.pathname || '/';

  // Auto-redirect if user is already authenticated
  useEffect(() => {
    // Only redirect if this is a user interaction, not a bot/crawler
    const isBot = /bot|crawler|spider|crawling/i.test(navigator.userAgent);
    
    if (currentUser && !authLoading && !isBot) {
      navigate('/dashboard', { replace: true });
    }
  }, [currentUser, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setLoginLoading(true);
      trackLoginStarted('email');
      
      const user = await login(formData.email, formData.password);
      trackLoginCompleted('email', user.uid);
      
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (error: any) {
      logger.error('Login error:', error);
      trackAuthError('email_login', error.message);
      
      // Use user-friendly message if available, otherwise handle specific Firebase auth errors
      if (error.userFriendlyMessage) {
        toast.error(error.userFriendlyMessage);
      } else if (error.code === 'auth/user-not-found') {
        toast.error('No account found with this email');
      } else if (error.code === 'auth/wrong-password') {
        toast.error('Incorrect password');
      } else if (error.code === 'auth/invalid-email') {
        toast.error('Invalid email address');
      } else if (error.code === 'auth/too-many-requests') {
        toast.error('Too many failed attempts. Please try again later');
      } else {
        toast.error('Failed to sign in. Please try again');
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setGoogleLoading(true);
      trackLoginStarted('google');
      
      const user = await loginWithGoogle();
      trackLoginCompleted('google', user.uid);
      
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (error: any) {
      trackAuthError('google_login', error.message);
      
      // Only log errors in development
      if (process.env.NODE_ENV === 'development') {
        logger.error('Google login error:', error);
      }
      
      if (error.code === 'auth/popup-closed-by-user') {
        toast.error('Sign-in was cancelled');
      } else if (error.code === 'auth/popup-blocked') {
        toast.error('Popup was blocked. Please allow popups and try again');
      } else {
        toast.error('Failed to sign in with Google');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <>
      <SEOHead 
        title="Sign In - ClipThis"
        description="Sign in to your ClipThis account to create and manage viral clips with AI."
      />
      <div className="auth-page">
      <div className="auth-container min-h-screen flex">
        {/* Left side - Form */}
        <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 lg:px-16 xl:px-20 max-w-md mx-auto w-full relative">
          {/* Animated dot background */}
          <div className="absolute inset-0 dot-pattern-animated opacity-40 pointer-events-none" />
          
          <div className="animate-fade-up relative z-10">
            {/* Back button */}
            <Link 
              to="/"
              className="inline-flex items-center text-sm text-primary-600 hover:text-black transition-colors mb-8"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to home
            </Link>

            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-display font-bold tracking-tight mb-2">
                Welcome back
              </h1>
              <p className="text-primary-600">
                Sign in to your ClipThis account
              </p>
            </div>

            {/* Google Sign In */}
            <button
              onClick={handleGoogleLogin}
              disabled={googleLoading || loginLoading}
              className="w-full flex items-center justify-center space-x-3 py-3 px-4 border border-primary-200 rounded-xl hover:border-primary-300 hover:bg-primary-50 transition-colors mb-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {googleLoading ? (
                <span className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              ) : (
                <>
                  <Chrome size={20} />
                  <span className="font-medium">Continue with Google</span>
                </>
              )}
            </button>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-primary-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-primary-500">or</span>
              </div>
            </div>

            {/* Email/Password Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label">Email</label>
                <div className="relative">
                  <Mail size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="input-field pl-11"
                    placeholder="Enter your email"
                    disabled={loginLoading || googleLoading}
                  />
                </div>
              </div>

              <div>
                <label className="label">Password</label>
                <div className="relative">
                  <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="input-field pl-11 pr-11"
                    placeholder="Enter your password"
                    disabled={loginLoading || googleLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-400 hover:text-primary-600 transition-colors"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-primary-300 text-black focus:ring-black"
                  />
                  <span className="ml-2 text-sm">Remember me</span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-red-600 hover:text-red-700 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loginLoading || googleLoading}
                className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loginLoading ? (
                  <span className="loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            {/* Sign up link */}
            <p className="text-center text-sm text-gray-600 mt-6">
              Don't have an account?{' '}
              <Link
                to="/signup"
                className="font-medium text-red-600 hover:text-red-700 transition-colors"
              >
                Sign up
              </Link>
            </p>
          </div>
        </div>

                {/* Right side - Feature showcase */}        <div className="hidden lg:flex flex-1 bg-black text-white flex-col justify-center px-12 xl:px-16 relative">          {/* Animated dots on right side too */}          <div className="absolute inset-0 dot-pattern-animated-dark pointer-events-none" />
          <div className="animate-fade-up relative z-10" style={{ animationDelay: '0.1s' }}>
            <h2 className="text-4xl font-display font-bold mb-6">
              Create viral clips in minutes
            </h2>
            <p className="text-lg text-gray-300 mb-8">
              AI-powered video analysis that finds the best moments in your streams automatically.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3 stagger-item">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse-subtle" />
                <span>Smart highlight detection</span>
              </div>
              <div className="flex items-center space-x-3 stagger-item">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse-subtle" />
                <span>Emotion and audio analysis</span>
              </div>
              <div className="flex items-center space-x-3 stagger-item">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse-subtle" />
                <span>Professional quality exports</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Login; 